// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import GlobalLoader from "@/components/common/BrandLogo";
import BillingSkeleton from "@/components/organization/skeletons/BillingSkeleton";
import { toast } from "sonner";
import { Check, Loader2, AlertCircle, Download, ExternalLink, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import SectionCard from "@/components/common/SectionCard";
import { useGetBillingPlans, useGetBillingStatus, usePostCheckout, useGetCheckoutStatus, usePostPortal } from "@/hooks/useBilling";
import AddonsCard from "@/components/billing/AddonsCard";
import {
  BILLING_PLAN_FEATURES,
  BILLING_STATUS_STYLES,
} from "@/constant/organization.constant";
import { CheckoutSessionStatus, InternalRole, OrganizationStatus, PaymentStatus } from "@/enum/common.enum";
import { formatCurrency, formatUtcDate } from "@/utils/common.utils";
import { PUBLIC_ROUTES, PROTECTED_ROUTES } from "@/routes/url";
import { apiErrorMessage, isApiErrorHandled } from "@/services/api";

export default function Billing() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { user, context, refreshContext } = useAuth();
  const [checkoutSource, setCheckoutSource] = useState(null);
  const [polling, setPolling] = useState(false);

  const isOwner = context?.membership?.role === InternalRole.ORG_OWNER || context?.membership?.role === InternalRole.BROKERAGE_ADMIN;

  const { data: plansData } = useGetBillingPlans();
  const { data: billing, refetch: refetchBilling } = useGetBillingStatus();
  const plans = plansData?.plans ?? [];
  const { mutateAsync: postCheckout } = usePostCheckout();
  // Plan changes, card updates and cancellation all happen in Stripe's hosted
  // portal rather than here — it stays compliant without us maintaining it, and
  // every action there emits the webhooks the backend already syncs from.
  const { mutate: openPortal, isPending: portalBusy } = usePostPortal();
  const manageBilling = () => openPortal({ origin_url: window.location.origin });
  // `stripe_customer_id` alone isn't proof of a real subscription — Stripe
  // Checkout creates the customer record before payment completes, so an
  // abandoned checkout (Phase 14: "Checkout abandonment") leaves a customer_id
  // behind with nothing to manage. `stripe_subscription_id`, surfaced here as
  // `is_subscribed`, only exists once a subscription actually activates — that's
  // what decides whether "Manage subscription" has a real portal to open (BUG-006).
  const hasSubscription = Boolean(billing?.is_subscribed);
  const sessionId = search.get("session_id");
  const { refetch: refetchCheckoutStatus } = useGetCheckoutStatus(sessionId || "", { enabled: false });

  useEffect(() => {
    const sid = sessionId;
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      if (attempts > 10) { setPolling(false); toast.error("Payment status check timed out. Refresh in a moment."); return; }
      try {
        const { data } = await refetchCheckoutStatus();
        if (!data) { setTimeout(tick, 2500); return; }
        // A $0 first invoice (a carried-over trial, or a 100% promo code)
        // completes as `no_payment_required`, never `paid` — both mean live.
        if (data.status === CheckoutSessionStatus.COMPLETE
            && (data.payment_status === PaymentStatus.PAID || data.payment_status === PaymentStatus.NO_PAYMENT_REQUIRED)) {
          toast.success("Subscription activated");
          setPolling(false);
          await refreshContext();
          await refetchBilling();
          search.delete("session_id"); setSearch(search, { replace: true });
          return;
        }
        if (data.status === CheckoutSessionStatus.EXPIRED) { toast.error("Session expired"); setPolling(false); return; }
        setTimeout(tick, 2000);
      } catch (e) { setTimeout(tick, 2500); }
    };
    tick();
    // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => {
    if (search.get("canceled")) {
      toast.info("Checkout canceled");
      search.delete("canceled"); setSearch(search, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const checkout = async (plan_id, source, addons) => {
    setCheckoutSource(source);
    try {
      const { data } = await postCheckout({ plan_id, origin_url: window.location.origin, addons });
      if (!data) throw new Error("Could not start checkout");
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // Existing subscription — the backend swapped the plan item in place
      // rather than opening a new Checkout Session, so there's no redirect.
      toast.success("Your plan has been updated");
      refetchBilling();
      setCheckoutSource(null);
    } catch (e) {
      setCheckoutSource(null);
      // A Stripe failure (502) isn't toasted by the API layer, and swallowing
      // it left the button spinning back to idle with no reason given.
      if (!isApiErrorHandled(e)) toast.error(apiErrorMessage(e));
    }
  };

  if (!billing) return <BillingSkeleton />;

  if (slug && context?.organization?.slug && slug !== context.organization.slug) {
    return <ShellRedirect to={`/o/${context.organization.slug}/billing`} />;
  }

  const status = billing.status;
  const style = BILLING_STATUS_STYLES[status] || BILLING_STATUS_STYLES.trialing;
  const usage = billing.usage || {};
  // Cheapest first, so "the next tier up" is a position rather than a guess.
  const publicPlans = plans.filter(p => p.public_checkout).sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0));
  const contactPlans = plans.filter(p => !p.public_checkout);

  // `billing.plan` is the literal string "free" until someone subscribes, which
  // matches no entry in BILLING_PLANS. Treating that as the current plan is what
  // made a trialling org read "Current plan: Solo" while simultaneously being
  // offered an upgrade *to* Solo.
  const isSubscribed = Boolean(billing.is_subscribed);
  const currentPlan = isSubscribed ? plans.find(p => p.id === billing.plan) : undefined;

  // Offer the next tier *up* by price. The old rule was "the first public plan
  // whose id differs from the current one", which offered a Team subscriber
  // "Upgrade to Solo" — a downgrade, mislabelled.
  const currentAmount = currentPlan?.amount ?? -1;
  const upgradePlan = publicPlans.find(p => (p.amount ?? 0) > currentAmount) || contactPlans[0];

  // During the trial the limits are Individual's, but the plan is not — say
  // "Free trial" and let the limits below speak for what's included.
  const planName = currentPlan?.name || (status === OrganizationStatus.TRIALING ? "Free trial" : usage.plan_name || "No plan");
  const planPrice = currentPlan?.amount;
  const trialPlanName = billing.effective_plan_name || usage.plan_name;
  // All real now. `sms_used` / `automation_runs` come back as null rather than 0
  // because nothing meters them — a zero would read as "none used yet", so the
  // rows are hidden entirely instead of showing a number that means nothing.
  // Whichever ran out is the date worth naming: active_until for a lapsed
  // subscription, trial_end for a lapsed trial.
  const lapsedStamp = billing.active_until || billing.trial_end || null;
  const lapsedOn = lapsedStamp ? formatUtcDate(lapsedStamp) : null;
  const paymentMethod = billing.payment_method || null;
  const invoices = billing.invoices ?? [];
  const storageUsed = usage.storage_used_gb ?? 0;
  const storageLimit = usage.storage_limit_gb ?? null;
  const hasSmsMetering = usage.sms_used !== null && usage.sms_used !== undefined;
  const hasAutomationMetering = usage.automation_runs !== null && usage.automation_runs !== undefined;
  // "assistant" on Individual, "admin" on Team/Growth, null where the plan has
  // no separate seat (Brokerage/Enterprise are uncapped anyway).
  const supportLabel = usage.support_seat_label || null;

  // New signups land here first, as a page of its own (DashboardLayout drops the
  // sidebar): the card goes on file through Stripe Checkout, which carries the
  // trial over, so today's charge is $0. Plan usage, add-ons and invoices mean
  // nothing until then, so they wait.
  if (billing.needs_card) {
    const firstName = (user?.name || "").trim().split(/\s+/)[0];
    // Not <Shell>: its min-h-screen sits under DashboardLayout's own logo bar
    // here, so the page always overflowed into a screen of empty scroll.
    return (
      <div className="font-dashboard">
        <div className="w-full" data-testid="billing-page">
          {polling ? (
            <p className="flex items-center justify-center gap-2 py-24 text-sm text-dash-muted"><Loader2 className="size-4 animate-spin" /> Confirming your card with Stripe…</p>
          ) : (
          <section className="rounded-2xl border border-dash-border bg-white p-6 sm:p-10" data-testid="billing-start-trial">
            {firstName && <p className="mb-2 text-sm font-medium text-dash-greenDeep" data-testid="billing-welcome">Welcome to Stoop, {firstName}</p>}
            <h1 className="font-display text-3xl tracking-tight text-dash-ink sm:text-4xl">Start your 14-day free trial</h1>
            <p className="mt-2 text-sm text-dash-muted">
              Choose a plan and add a card. You won&apos;t be charged until your trial ends
              {billing.trial_end ? ` on ${formatUtcDate(billing.trial_end)}` : ""} — cancel any time before then.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {publicPlans.map((plan, index) => (
                // The cheapest plan (Individual) is the suggested start: it fits
                // a solo agent, and moving up to Team later is one click.
                <div key={plan.id} className={`flex flex-col rounded-xl border p-4 ${index === 0 ? "border-2 border-dash-greenDeep" : "border-dash-border"}`} data-testid={`start-trial-${plan.id}`}>
                  {index === 0 && <span className="mb-2 self-start rounded-full bg-dash-hover px-2.5 py-0.5 text-[11px] font-semibold text-dash-greenDeep" data-testid="start-trial-suggested">Most agents start here</span>}
                  <p className="text-sm font-semibold text-dash-ink">{plan.name}</p>
                  <p className="mt-1 text-sm font-bold text-dash-ink">${Math.round(plan.amount)}/month</p>
                  <ul className="mt-3 flex-1 space-y-2 text-xs text-dash-muted">
                    {(BILLING_PLAN_FEATURES[plan.id] || []).map(feature => <li key={feature} className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0 text-dash-greenMid" />{feature}</li>)}
                  </ul>
                  <Button type="button" variant="dashboardPrimary" onClick={() => checkout(plan.id, plan.id)} disabled={!isOwner || checkoutSource != null} loading={checkoutSource === plan.id} className="mt-4 w-full">
                    Start free trial
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-xs text-dash-muted" data-testid="start-trial-reassurance">
              <Lock className="size-3.5 shrink-0" aria-hidden="true" />
              Secure checkout by Stripe · $0 today · Cancel any time{billing.trial_end ? ` before ${formatUtcDate(billing.trial_end)}` : ""}
            </p>
            {!isOwner && <p className="mt-3 text-xs text-dash-faint">Only owners and admins can manage billing.</p>}
          </section>
          )}
        </div>
      </div>
    );
  }

  return (
    <Shell>
      <div className="w-full" data-testid="billing-page">
        <PageHeader className="mb-10" title="Plan & billing" description="Manage your subscription, payment method, and plan usage." actions={polling ? <span className="inline-flex items-center gap-2 text-xs text-dash-muted"><Loader2 className="size-3.5 animate-spin" /> Confirming payment…</span> : null} />

        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(290px,1fr)]">
          <div className="space-y-5">
            <section className="rounded-2xl border border-dash-border bg-white p-6" data-testid="billing-status-card">
              <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-dash-faint">Current plan</p>
                    <StatusBadge tone={style.tone} className="text-[10px]" data-testid="billing-status-badge">{style.label}</StatusBadge>
                  </div>
                  <h2 className="mt-1 text-xl font-bold text-dash-ink">{planName}</h2>
                  <p className="mt-1 text-sm text-dash-muted">
                    {typeof planPrice === "number"
                      ? `$${Math.round(planPrice)}/month · Billed monthly`
                      : status === OrganizationStatus.TRIALING
                        // Name what the trial actually includes, rather than the
                        // contentless "Flexible billing" this used to show.
                        ? `Free while you try Stoop · ${trialPlanName} limits, no card needed`
                        : "No active subscription"}
                    {typeof billing.days_left === "number" ? ` · ${status === OrganizationStatus.TRIALING ? "Trial ends in" : "Renews in"} ${billing.days_left} day${billing.days_left === 1 ? "" : "s"}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {hasSubscription
                      ? <Button variant="dashboardOutline" type="button" onClick={manageBilling} loading={portalBusy} disabled={!isOwner} data-testid="billing-manage">Manage subscription</Button>
                      : <Button variant="dashboardOutline" asChild><Link to={PUBLIC_ROUTES.PRICING}>Change plan</Link></Button>}
                  {upgradePlan?.public_checkout && (
                    <Button type="button" variant="dashboardPrimary" onClick={() => checkout(upgradePlan.id, "header")} disabled={polling || !isOwner || checkoutSource != null} loading={checkoutSource === "header"}>
                      Upgrade to {upgradePlan.name}
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-4 border-t border-dash-border pt-5 sm:grid-cols-4">
                {/* The effective caps — plan base plus anything bought — with
                    the split spelled out, so a Solo plan reading "8 deals"
                    explains itself rather than looking like a bug. */}
                <PlanLimit label="Agent seats" value={usage.seats_limit} base={usage.seats_limit_base} fromAddons={usage.seats_from_addons} />
                {/* The plan's one assistant (Individual) / admin (Team,
                    Growth) seat, on top of the agent seats. */}
                {supportLabel && (
                  <PlanLimit label={`${capitalize(supportLabel)} seat`} value={usage.support_seats_limit ?? 0} />
                )}
                {/* null = no cap (Team, Growth) → "Unlimited". */}
                <PlanLimit label="Active deals" value={usage.deals_limit} base={usage.deals_limit_base} fromAddons={usage.deals_from_addons} />
                {/* A null limit is an uncapped plan, which PlanLimit renders as
                    "Unlimited". Interpolating it into a template string instead
                    would print the literal "null GB". */}
                <PlanLimit label="File storage" value={storageLimit === null ? null : `${storageLimit} GB`} />
                {hasAutomationMetering && (
                  <PlanLimit label="Automations" value={usage.automation_limit === null ? null : `${usage.automation_limit}/month`} />
                )}
              </div>
              {/* A lapsed subscription and a hit plan limit are different
                  problems with different fixes, and the old copy ("Action is
                  needed…") covered both without saying which. Split them. */}
              {(status === OrganizationStatus.PAST_DUE || status === OrganizationStatus.CANCELED) && (
                <div className="mt-5 rounded-xl border border-dash-rust/25 bg-dash-rust/10 px-4 py-4" data-testid="billing-lapsed-notice">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="mt-0.5 size-4 shrink-0 text-dash-rust" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-dash-rust">
                        {status === OrganizationStatus.CANCELED ? "Your subscription was canceled" : "Your trial has ended"}
                        {lapsedOn ? ` on ${lapsedOn}` : ""}.
                      </p>
                      <p className="mt-1 text-sm leading-6 text-dash-rust/80">
                        Starting new deals and sending invitations is paused. Everything already in flight keeps
                        working normally — you and your clients can still update tasks, upload documents and message
                        each other. Upgrading lifts the pause immediately.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Deliberately keyed on the DEAL limit alone. The Individual plan
                  includes exactly one agent seat, which the owner occupies from
                  the moment the org is created — so `at_seat_limit` is true for
                  every Individual org forever, and alarming on it told brand-new accounts with zero
                  deals that they had "reached a plan limit". Running out of
                  deals genuinely blocks the core action; using the one seat you
                  bought does not. The seat row in Usage below still shows 1 of 1,
                  and the invite buttons explain themselves when disabled. */}
              {status !== OrganizationStatus.PAST_DUE && status !== OrganizationStatus.CANCELED
                && usage.at_deal_limit && (
                <div className="mt-5 flex items-start gap-2 rounded-xl bg-rust/10 px-4 py-3 text-sm text-rust" data-testid="billing-at-limit-warning">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  You&apos;re using all {usage.deals_limit} active deals.{" "}
                  {usage.deal_addon_available ?? billing.addons_available
                    ? <>Add more below, close one, or upgrade to open more.</>
                    : <>Close or archive one, or upgrade to open more.</>}
                </div>
              )}
            </section>

            <SectionCard className="rounded-2xl" title="Usage this billing period" data-testid="billing-usage">
              <div className="space-y-4">
                <UsageStat label="Agent seats" used={usage.seats_used ?? 0} limit={usage.seats_limit} fromAddons={usage.seats_from_addons} at={usage.at_seat_limit} near={usage.near_seat_limit} testId="usage-seats" />
                {supportLabel && (
                  <UsageStat label={`${capitalize(supportLabel)} seat`} used={usage.support_seats_used ?? 0} limit={usage.support_seats_limit ?? 0} testId="usage-support-seat" />
                )}
                <UsageStat label="Active deals" used={usage.deals_active ?? 0} limit={usage.deals_limit} fromAddons={usage.deals_from_addons} at={usage.at_deal_limit} near={usage.near_deal_limit} testId="usage-deals" />
                <UsageStat label="File storage" used={storageUsed} limit={storageLimit} testId="usage-storage" />
                {hasSmsMetering && <UsageStat label="SMS messages" used={usage.sms_used} limit={usage.sms_limit} testId="usage-sms" />}
                {hasAutomationMetering && <UsageStat label="Automation runs" used={usage.automation_runs} limit={usage.automation_limit} testId="usage-automations" />}
              </div>
            </SectionCard>

            {/* An org still on the trial has no subscription for an add-on item
                to attach to, so its picker carries the selection into the same
                Checkout Session as the plan — one subscription, one invoice. */}
            <AddonsCard
              billing={billing}
              isOwner={isOwner}
              onApplied={refetchBilling}
              checkoutBusy={checkoutSource === "addons"}
              onCheckout={addons => checkout((upgradePlan?.public_checkout ? upgradePlan.id : publicPlans[0]?.id), "addons", addons)}
            />

            <section className="overflow-hidden rounded-2xl border border-dash-border bg-white" data-testid="billing-invoice-history">
              <div className="flex items-center justify-between border-b border-dash-border px-6 py-4"><h2 className="text-sm font-semibold text-dash-ink">Invoice history</h2><Button variant="seamless" type="button" onClick={manageBilling} disabled={!hasSubscription || !isOwner} className="text-xs font-medium text-dash-greenDeep hover:underline disabled:opacity-40">View all in Stripe</Button></div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-left">
                  <thead><tr className="border-b border-dash-border text-[10px] uppercase tracking-wider text-dash-faint"><th className="px-6 py-3 font-medium">Invoice</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Amount</th><th className="px-4 py-3 font-medium">Status</th><th className="px-6 py-3" /></tr></thead>
                  <tbody>{invoices.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-dash-faint">No invoices yet. They appear here once your first payment is processed.</td></tr>
                  ) : invoices.map(invoice => (
                    <tr key={invoice.id} className="border-b border-dash-border last:border-0">
                      <td className="px-6 py-4 text-sm font-medium text-dash-ink">{invoice.number || invoice.id}</td><td className="px-4 py-4 text-sm text-dash-muted">{invoice.date}</td><td className="px-4 py-4 text-sm font-semibold text-dash-ink">{formatCurrency(invoice.amount / 100) ?? "—"}</td><td className="px-4 py-4"><span className="rounded-full bg-dash-hover px-2 py-1 text-[10px] font-semibold text-dash-greenMid">{invoice.status}</span></td><td className="px-6 py-4 text-right">{invoice.invoice_pdf ? <a href={invoice.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-dash-greenDeep hover:underline"><Download className="size-3.5" /> Download</a> : <span className="text-xs text-dash-faint">—</span>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          </div>

          <aside className="space-y-5">
            <section className="rounded-2xl border border-dash-border bg-white p-5">
              <div className="flex items-center justify-between"><h2 className="text-sm font-semibold text-dash-ink">Payment method</h2><Button variant="seamless" type="button" onClick={manageBilling} disabled={!hasSubscription || !isOwner} className="text-xs font-medium text-dash-greenDeep hover:underline disabled:opacity-40">Update</Button></div>
              {paymentMethod ? (
                <div className="mt-4 flex items-center gap-3 rounded-xl border border-dash-border bg-dash-searchBg p-4">
                  <span className="flex h-8 w-10 items-center justify-center rounded-md border border-dash-border bg-white text-[10px] font-bold uppercase text-dash-slate">{paymentMethod.brand}</span>
                  <div><p className="text-sm font-medium capitalize text-dash-ink">{paymentMethod.brand} ending in {paymentMethod.last4}</p><p className="mt-0.5 text-xs text-dash-faint">Expires {paymentMethod.expires}</p></div>
                </div>
              ) : (
                <p className="mt-4 rounded-xl border border-dashed border-dash-border p-4 text-sm text-dash-faint">No card on file yet.</p>
              )}
            </section>

            {upgradePlan && (
              <section className="rounded-2xl border border-dash-border bg-white p-5" data-testid={`plan-card-${upgradePlan.id}`}>
                <h2 className="text-sm font-semibold text-dash-ink">Upgrade to {upgradePlan.name}</h2>
                <ul className="mt-5 space-y-3 text-sm text-dash-ink">
                  {(BILLING_PLAN_FEATURES[upgradePlan.id] || []).map(feature => <li key={feature} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-dash-greenMid" />{feature}</li>)}
                </ul>
                {typeof upgradePlan.amount === "number" ? (
                  <>
                    <p className="mt-5 text-sm font-bold text-dash-ink">${Math.round(upgradePlan.amount)}/month</p>
                    <Button type="button" variant="dashboardPrimary" onClick={() => checkout(upgradePlan.id, "sidebar")} disabled={polling || !isOwner || checkoutSource != null} loading={checkoutSource === "sidebar"} data-testid={`upgrade-${upgradePlan.id}`} className="mt-4 w-full">Upgrade to {upgradePlan.name}</Button>
                    <p className="mt-3 text-center text-xs text-dash-faint">Contact sales for annual pricing</p>
                  </>
                ) : (
                  <Button variant="dashboardOutline" className="mt-5 w-full" asChild><Link to={`${PUBLIC_ROUTES.PRICING}#contact-form`}>Contact sales <ExternalLink data-icon="inline-end" /></Link></Button>
                )}
                {!isOwner && <p className="mt-3 text-center text-xs text-dash-faint">Only owners and admins can manage billing.</p>}
              </section>
            )}
          </aside>
        </div>

        <Button variant="seamless" onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)} className="mt-8 text-sm text-dash-muted hover:text-dash-greenDeep" data-testid="back-to-dashboard">← Back to dashboard</Button>
      </div>
    </Shell>
  );
}

function UsageStat({ label, used, limit, fromAddons, testId }) {
  const showBar = typeof limit === "number";
  const pct = showBar ? Math.min(100, Math.round((used / Math.max(1, limit)) * 100)) : 0;
  const barColor = "bg-primary";
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="text-dash-muted">{label}</span>
        <span className="font-semibold text-dash-ink">{used} <span className="font-normal text-dash-faint">{showBar ? `of ${limit} (${pct}%)${fromAddons > 0 ? ` · ${fromAddons} added` : ""}` : "· unlimited"}</span></span>
      </div>
      {showBar && (
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-dash-chip">
          <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function PlanLimit({ label, value, base, fromAddons }) {
  // Only worth splitting when an add-on actually contributes. "5 plan + 0
  // add-on" on every untouched row would be noise on three quarters of pages.
  const showSplit = typeof value === "number" && typeof base === "number" && fromAddons > 0;
  return (
    <div>
      <p className="text-xs text-dash-faint">{label}</p>
      <p className="mt-1 text-sm font-semibold text-dash-ink">{typeof value === "number" ? `Up to ${value}` : value ?? "Unlimited"}</p>
      {showSplit && <p className="mt-0.5 text-[11px] text-dash-faint">{base} plan + {fromAddons} add-on</p>}
    </div>
  );
}

function capitalize(word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

function Shell({ children }) {
  return <div className="min-h-screen bg-dash-page font-dashboard">{children}</div>;
}

function ShellRedirect({ to }) {
  const nav = useNavigate();
  useEffect(() => { nav(to, { replace: true }); }, [to, nav]);
  return <GlobalLoader fullScreen={false} label="Redirecting to billing…" className="bg-dash-page" />;
}
